import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';

import styles from './game-predictions.module.scss';
import { NflPredictionsService } from 'src/shared/services/nfl-predictions.service';
import { IGamePrediction } from 'src/shared/models/interfaces/ipredictions';
import { useHttpService } from 'src/shared/hooks/use-http-service';
import { NFL_TEAM_NICKNAMES } from 'src/shared/constants/nfl-teams';
import { NFLTeamAbbreviation } from 'src/shared/enums/nfl-abbreviations';
import { Grid, Loader } from 'semantic-ui-react';
import { addAlpha } from '../team/table.utils';
import { IconCalendarEvent } from '@tabler/icons-react';

const GamePredictions: FunctionComponent<{ season: number; week?: number }> = ({ season, week }) => {
    const predictionsService = useHttpService(NflPredictionsService);
    const [predictions, setPredictions] = useState<IGamePrediction[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        if (!week) return;
        try {
            setLoading(true);
            const gamePredictions = await predictionsService.getGamePredictions(season, week, week);
            setPredictions(gamePredictions);
        } finally {
            setLoading(false);
        }
    }, [season, week]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const Team: FunctionComponent<{ value: string }> = ({ value }) => {
        return (
            <div
                style={{
                    flexDirection: 'row',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <img
                    src={require(`../../../../assets/images/nfl/${value}.png`)}
                    width={40}
                    height={30}
                    style={{ marginRight: 5, paddingLeft: 5, objectFit: 'contain' }}
                />
                <div style={{ fontWeight: 600, fontSize: 15 }}>{NFL_TEAM_NICKNAMES[value as NFLTeamAbbreviation]}</div>
            </div>
        );
    };

    const colorize = (pct: number) => {
        if (pct < 50) return addAlpha('#ff4041', (50 - pct) / 30);
        else if (pct > 50) return addAlpha('#38c172', (pct - 50) / 40);
        else return 'transparent';
    };

    const gameDate = (date?: string) => {
        if (!date) return 'TBD';
        const dt = new Date(date);
        const dateLocaleString = dt.toLocaleString('en-us', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
        });
        const dateTimeString = dt.toLocaleTimeString('en-us', { hour: 'numeric', minute: '2-digit' });
        return `${dateLocaleString} ${dateTimeString}`;
    };

    return (
        <div className={styles.container}>
            <div className={styles.title}>Week {week}</div>
            {loading ? (
                <div className={styles.loader}>
                    <Loader active={true} size="big" inline />
                </div>
            ) : (
                <Grid padded centered>
                    {predictions
                        .sort((a, b) => a.game_key - b.game_key)
                        .map((game) => {
                            return (
                                <Grid.Column key={game.game_key} width={4}>
                                    <div className={styles.gameContainer}>
                                        <div className={styles.row}>
                                            <div className={styles.teamContainer}>
                                                <Team value={game.home_team_id} />
                                            </div>
                                            <div
                                                className={styles.winProb}
                                                style={{ backgroundColor: colorize(game.home_win_pct) }}
                                            >
                                                {game.home_win_pct.toFixed(1)}%
                                            </div>
                                        </div>
                                        <div className={styles.row}>
                                            <div className={styles.teamContainer}>
                                                <Team value={game.visitor_team_id} />
                                            </div>
                                            <div
                                                className={styles.winProb}
                                                style={{ backgroundColor: colorize(game.visitor_win_pct) }}
                                            >
                                                {game.visitor_win_pct.toFixed(1)}%
                                            </div>
                                        </div>
                                        <div className={styles.scheduleInfo}>
                                            <IconCalendarEvent size={16} color="#888" />
                                            <div className={styles.date}>{gameDate(game.schedule?.gameTime)}</div>
                                        </div>
                                    </div>
                                </Grid.Column>
                            );
                        })}
                </Grid>
            )}
        </div>
    );
};

export default GamePredictions;
